import {handleError, reorderDescendingByPosition, toastSuccess} from "helpers"
import {KlassappTableHOC} from "HOC"
import {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {DragDropContext, Draggable, Droppable, DropResult} from "react-beautiful-dnd"
import {v4 as uuidv4} from "uuid"
import {academicPlansService, generalSettingsService} from "services"
import {KlassappTableProps, YesNo} from "types/common"
import {ConfirmPopup} from "uiKit"
import styles from "./ClockHoursTranscript.module.css"
import {Major} from "types/major"
import {Col, Row} from "antd"
import {FormLabel} from "components/Form"
import {AddItemCircleButton, BaseButton, BaseInput, BaseLoading, Icon} from "components"
import {Course} from "types/courses"
import {cloneDeep} from "lodash"
import {useMutation, useVisible} from "hooks"
import {AddCoursesPopup} from "./AddCoursesPopup"
import {useQuery} from "@tanstack/react-query"
import {GeneralSettingField} from "types/settings/general-setting"
import {ClockHoursTranscriptSubmodule} from "types/clock-hours-transcript"
import {TranscriptCourseList} from "./TranscriptCourseList"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {ClockHoursTranscriptSectionEditPayload} from "types/clock-hours-transcript"

type ProgramTableProps = KlassappTableProps & {
    selectedProgram: Major.MajorVersion
}

type SectionDetail = {
    sectionId: string | number
    sectionTitle?: string
    position?: number | null
    courses: Array<SectionCourse>
}

export type SectionCourse = {
    courseId: number
    sectionCourseId: string | number
    position?: number | null
    deleteHtml?: React.ReactElement
}

const SectionView = (props: ProgramTableProps) => {
    const {data, dispatch, dispatchFunc, selectedProgram, page, pageSize} = props
    const addCoursesPopup = useVisible()
    const confirmSyncPopup = useVisible()
    const [currentSection, setCurrentSection] = useState<SectionDetail>()
    const [loading, setLoading] = useState(false)
    const [initialSections, setInitialSections] = useState<Array<SectionDetail>>([])
    const [sections, setSections] = useState<Array<SectionDetail>>([])
    const [submodulesByCourseId, setSubmodulesByCourseId] = useState<Record<number, ClockHoursTranscriptSubmodule[]>>(
        {}
    )
    const sectionsData = useRef<Array<SectionDetail>>([])

    const editSection = useMutation(academicPlansService.editClockHoursTranscriptSections, {
        onSuccess: () => {
            getData()
            toastSuccess("Updated Successfully")
            setLoading(false)
        },
        onError: (err) => {
            setLoading(false)
            handleError(err, true)
        }
    })

    const syncSections = useMutation(academicPlansService.syncClockHoursTranscriptSections, {
        onSuccess: () => {
            getData()
            toastSuccess("Synced with program version")
            setLoading(false)
            confirmSyncPopup.close()
        },
        onError: (err) => {
            setLoading(false)
            handleError(err, true)
        }
    })

    const {isFetching: isFetchingSections, refetch: refetchSections} = useQuery(
        ["clock-hours-transcript-sections", selectedProgram.id, page, pageSize],
        () =>
            academicPlansService.listClockHoursTranscriptSections({
                filter: {
                    programVersionId: selectedProgram.id
                },
                range: {
                    page,
                    pageSize: UNLIMITED_PAGE_SIZE
                }
            }),
        {
            onSuccess: ({data, total}) => {
                data = data.map((section) => {
                    if (section?.courses) {
                        section.courses = section.courses.map((course) => {
                            return {
                                ...course,
                                deleteHtml: (
                                    <div
                                        onClick={() => {
                                            onDeleteCourse(course.courseId, section.sectionId)
                                        }}
                                        className={styles.trashButton}>
                                        <Icon icon="TRASH_LINE" />
                                    </div>
                                )
                            }
                        })
                    }
                    return section
                })
                setInitialSections(cloneDeep(data))
                setSections(cloneDeep(data))
                dispatch({data: data, total})
            },
            onError: (err) => {
                handleError(err)
            },
            enabled: !!selectedProgram
        }
    )

    useEffect(() => {
        sectionsData.current = cloneDeep(sections)
    }, [sections])

    const courseIds = useMemo(() => {
        const ids = new Set<number>()
        for (const section of sections) {
            for (const course of section.courses) {
                if (typeof course.courseId === "number") {
                    ids.add(course.courseId)
                }
            }
        }
        return Array.from(ids)
    }, [sections])

    const showSubmodulesSetting = useQuery(
        ["clock-hours-transcript-show-submodules"],
        () => generalSettingsService.getSettings([GeneralSettingField.ClockHoursTranscriptShowSubmodules]),
        {onError: (err) => handleError(err)}
    )
    const showSubmodules =
        showSubmodulesSetting.data?.find(
            (item) => item.field === GeneralSettingField.ClockHoursTranscriptShowSubmodules
        )?.value === YesNo.Yes

    const submodulesQuery = useQuery(
        ["clock-hours-transcript-submodules", selectedProgram.id, courseIds],
        () =>
            academicPlansService.listClockHoursTranscriptSubmodules({
                programVersionId: selectedProgram.id,
                courseIds
            }),
        {
            enabled: showSubmodules && courseIds.length > 0,
            onError: (err) => handleError(err)
        }
    )

    useEffect(() => {
        const grouped: Record<number, ClockHoursTranscriptSubmodule[]> = {}
        for (const submodule of submodulesQuery.data ?? []) {
            if (!grouped[submodule.courseId]) {
                grouped[submodule.courseId] = []
            }
            grouped[submodule.courseId].push(submodule)
        }
        setSubmodulesByCourseId(grouped)
    }, [submodulesQuery.data])

    const getFields = () => {
        return ["Code", "Course Name", "Clock", ""]
    }

    const getPageTitle = () => {
        return "Major"
    }

    const getColumns = () => {
        return [
            {
                title: "Code",
                field: "courseCode",
                style: {minWidth: 300}
            },
            {
                title: "Course Name",
                field: "courseName",
                style: {minWidth: 300}
            },
            {
                title: "Clock",
                field: "credits",
                style: {minWidth: 40}
            },
            {
                title: "",
                field: "deleteHtml",
                style: {maxWidth: 40}
            }
        ]
    }

    const onDeleteCourse = (courseId: number, sectionId: number | string) => {
        const newSections = cloneDeep(sectionsData.current)
        for (const section of newSections) {
            if (section.courses.length && section.sectionId === sectionId) {
                section.courses = section.courses.filter((course) => course.courseId !== courseId)
            }
        }
        setSections(cloneDeep(newSections))
    }

    const getData = async () => {
        refetchSections()
    }
    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    const addSection = () => {
        setSections([
            ...sections,
            {
                sectionId: uuidv4(),
                courses: []
            }
        ])
    }

    const preparePayloadForSaving = (): ClockHoursTranscriptSectionEditPayload => {
        let payload: any = {}
        const initialSectionCourseIds = initialSections.reduce((sectionCourseIds, section) => {
            sectionCourseIds.push(...section.courses.map((course) => course.sectionCourseId))
            return sectionCourseIds
        }, [])
        const initialSectionIds = initialSections.reduce((sectionIds, section) => {
            sectionIds.push(section.sectionId)
            return sectionIds
        }, [])
        const newSectionIds = sections.reduce((sectionIds, section) => {
            sectionIds.push(section.sectionId)
            return sectionIds
        }, [])
        const newSectionCourseIds = sections.reduce((sectionCourseIds, section) => {
            sectionCourseIds.push(...section.courses.map((course) => course.sectionCourseId))
            return sectionCourseIds
        }, [])

        const deleteSectionCourseIds = initialSectionCourseIds.filter(
            (sectionCourseId) => !newSectionCourseIds.includes(sectionCourseId)
        )
        const deleteSectionIds = initialSectionIds.filter((sectionId) => !newSectionIds.includes(sectionId))
        payload = {
            sections: sections.map((section) => {
                return {
                    programVersionId: selectedProgram.id,
                    sectionId: typeof section.sectionId === "number" ? section.sectionId : undefined,
                    position: typeof section.position === "number" ? section.position : undefined,
                    courses: section.courses.map((course) => {
                        return {
                            courseId: course.courseId,
                            position: typeof course.position === "number" ? course.position : undefined,
                            sectionCourseId:
                                typeof course.sectionCourseId === "number" ? course.sectionCourseId : undefined
                        }
                    }),
                    sectionTitle: section.sectionTitle
                }
            }) as any,
            deleteSectionIds,
            deleteSectionCourseIds
        }
        return payload
    }

    const onSave = async () => {
        const payload = preparePayloadForSaving()
        try {
            const sections = payload.sections ?? []
            for (let section of sections) {
                if (!section.sectionTitle?.length) {
                    handleError({
                        message: "Title is required"
                    })
                    return
                }
            }
            setLoading(true)
            editSection.mutate(payload)
        } catch (err) {
            handleError(err)
        }
    }

    const onDeleteSection = (id: string | number) => {
        setSections(sections.filter((item) => item.sectionId !== id))
    }

    const persistPositions = async (payload: {
        sections?: Array<{sectionId: number; position: number}>
        courses?: Array<{sectionCourseId: number; position: number}>
    }) => {
        if (!payload.sections?.length && !payload.courses?.length) {
            return
        }
        try {
            await academicPlansService.updateClockHoursTranscriptPositions(payload)
        } catch (error) {
            handleError(error)
        }
    }

    const onSectionDragEnd = async (result: DropResult) => {
        const {destination, source} = result
        if (!destination || destination.index === source.index) {
            return
        }
        const originalPositionById = new Map(sections.map((section) => [section.sectionId, section.position]))
        const nextSections = reorderDescendingByPosition(sections, source.index, destination.index)
        setSections(nextSections)
        await persistPositions({
            sections: nextSections
                .filter(
                    (section) =>
                        typeof section.sectionId === "number" &&
                        section.position !== originalPositionById.get(section.sectionId)
                )
                .map((section) => ({
                    sectionId: section.sectionId as number,
                    position: section.position as number
                }))
        })
    }

    const onCourseDragEnd = async (sectionId: string, sourceIndex: number, destinationIndex: number) => {
        if (sourceIndex === destinationIndex) {
            return
        }
        const section = sections.find((item) => String(item.sectionId) === sectionId)
        if (!section) {
            return
        }
        const originalPositionById = new Map(section.courses.map((course) => [course.sectionCourseId, course.position]))
        const nextCourses = reorderDescendingByPosition(section.courses, sourceIndex, destinationIndex)
        setSections(
            sections.map((item) => (String(item.sectionId) === sectionId ? {...item, courses: nextCourses} : item))
        )
        await persistPositions({
            courses: nextCourses
                .filter(
                    (course) =>
                        typeof course.sectionCourseId === "number" &&
                        course.position !== originalPositionById.get(course.sectionCourseId)
                )
                .map((course) => ({
                    sectionCourseId: course.sectionCourseId as number,
                    position: course.position as number
                }))
        })
    }

    const onSubmoduleDragEnd = async (courseId: number, sourceIndex: number, destinationIndex: number) => {
        if (sourceIndex === destinationIndex) {
            return
        }
        const current = submodulesByCourseId[courseId] ?? []
        const originalPositionById = new Map(current.map((submodule) => [submodule.submoduleId, submodule.position]))
        const nextSubmodules = reorderDescendingByPosition(current, sourceIndex, destinationIndex)
        setSubmodulesByCourseId((previous) => ({...previous, [courseId]: nextSubmodules}))
        const changed = nextSubmodules.filter(
            (submodule) => submodule.position !== originalPositionById.get(submodule.submoduleId)
        )
        if (!changed.length) {
            return
        }
        try {
            await academicPlansService.updateClockHoursTranscriptSubmodulePositions({
                programVersionId: selectedProgram.id,
                courseId,
                submodules: changed.map((submodule) => ({
                    submoduleId: submodule.submoduleId,
                    position: submodule.position as number
                }))
            })
        } catch (error) {
            setSubmodulesByCourseId((previous) => ({...previous, [courseId]: current}))
            handleError(error)
        }
    }

    const onDragEnd = async (result: DropResult) => {
        const {destination, source, type} = result
        if (!destination || source.droppableId !== destination.droppableId) {
            return
        }
        if (type === "course") {
            await onCourseDragEnd(source.droppableId.slice("courses:".length), source.index, destination.index)
            return
        }
        if (type === "submodule") {
            const courseId = Number(source.droppableId.split(":")[2])
            await onSubmoduleDragEnd(courseId, source.index, destination.index)
            return
        }
        await onSectionDragEnd(result)
    }

    const onSync = () => {
        setLoading(true)
        syncSections.mutate({programVersionId: selectedProgram.id})
    }

    const showAddCoursesPopup = useCallback(() => {
        addCoursesPopup.open()
    }, [addCoursesPopup])

    const saveCoursesSuccess = (courses: Array<Course.Course & {sectionCourseId: number; sectionId: number}>) => {
        const newSections = cloneDeep(sections)
        const section = newSections.find((item) => item.sectionId === courses[0].sectionId)
        section.courses = courses.map((course) => {
            return {
                ...course,
                deleteHtml: (
                    <div
                        onClick={() => {
                            onDeleteCourse(course.courseId, course.sectionId)
                        }}
                        className={styles.trashButton}>
                        <Icon icon="TRASH_LINE" />
                    </div>
                )
            }
        })
        setSections(newSections)
    }

    const handleAddNewCourse = useCallback(
        (section: SectionDetail) => {
            setCurrentSection(section)
            showAddCoursesPopup()
        },
        [showAddCoursesPopup]
    )

    const isChanged = useMemo(() => {
        if (initialSections.length !== sections.length) {
            return true
        }
        for (let section of sections) {
            const initialSection = initialSections.find((item) => item.sectionId === section.sectionId)
            if (initialSection?.sectionTitle !== section.sectionTitle) {
                return true
            }
        }
        const courses = sections.reduce((courses, section) => {
            courses = [...courses, ...section.courses]
            return courses
        }, [])

        const initialCourses = initialSections.reduce((courses, section) => {
            courses = [...courses, ...section.courses]
            return courses
        }, [])
        if (courses.length !== initialCourses.length) {
            return true
        }
        return false
    }, [initialSections, sections])

    return (
        <>
            <DragDropContext onDragEnd={onDragEnd}>
                <div className={styles.headerActions}>
                    <BaseButton
                        title="Sync with Program Version"
                        variant="secondary"
                        loading={syncSections.isLoading}
                        onClick={confirmSyncPopup.open}
                    />
                </div>
                <Droppable droppableId="clock-hours-transcript-sections" type="section">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            {sections.map((section, index) => {
                                return (
                                    <Draggable
                                        key={section.sectionId}
                                        draggableId={`section-${section.sectionId}`}
                                        index={index}>
                                        {(dragProvided, snapshot) => (
                                            <div
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                className={`${styles.sectionCard}${
                                                    snapshot.isDragging ? ` ${styles.sectionDragging}` : ""
                                                }`}
                                                style={dragProvided.draggableProps.style}>
                                                <div className={styles.sectionHeader}>
                                                    <div
                                                        className={styles.dragHandle}
                                                        {...dragProvided.dragHandleProps}>
                                                        <Icon
                                                            icon="DRAG"
                                                            color="#939393"
                                                            className={styles.dragHandleIcon}
                                                        />
                                                    </div>
                                                    <div className={styles.sectionHeaderFields}>
                                                        <Row gutter={[12, 12]}>
                                                            <Col span={24}>
                                                                <div>
                                                                    <FormLabel label="Section title" />
                                                                    <BaseInput
                                                                        placeholder=""
                                                                        onChange={(text) => {
                                                                            section.sectionTitle = text
                                                                            setSections(cloneDeep(sections))
                                                                        }}
                                                                        value={section.sectionTitle}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </div>
                                                <TranscriptCourseList
                                                    sectionId={section.sectionId}
                                                    courses={section.courses}
                                                    showSubmodules={showSubmodules}
                                                    submodulesByCourseId={submodulesByCourseId}
                                                    onDeleteCourse={onDeleteCourse}
                                                />
                                                <div className={styles.actionWrap}>
                                                    <BaseButton
                                                        title="Delete Section"
                                                        variant="secondary"
                                                        icon={<Icon icon="DELETE" className={styles.deleteIcon} />}
                                                        uppercase={false}
                                                        onClick={() => onDeleteSection(section.sectionId)}
                                                    />
                                                    <AddItemCircleButton
                                                        title="Add Courses"
                                                        className={styles.addCourses}
                                                        onClick={() => handleAddNewCourse(section)}
                                                    />
                                                </div>

                                                {addCoursesPopup.isVisible && currentSection && (
                                                    <AddCoursesPopup
                                                        isShow={addCoursesPopup.isVisible}
                                                        onClose={addCoursesPopup.close}
                                                        selectedCourses={currentSection.courses}
                                                        sectionId={currentSection.sectionId}
                                                        saveCoursesSuccess={saveCoursesSuccess}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </Draggable>
                                )
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            <div className={styles.line} />
            <div className={styles.footerActions}>
                <BaseButton
                    title="Add Section"
                    icon={<Icon icon="PLUS" />}
                    variant="secondary"
                    onClick={addSection}
                    className={styles.addSectionBtn}
                />
            </div>
            <div className={styles.saveActionWrap}>
                <BaseButton
                    title="Save"
                    loading={loading}
                    disabled={!isChanged}
                    onClick={isChanged ? onSave : undefined}
                />
            </div>
            <BaseLoading isShow={isFetchingSections} />
            <ConfirmPopup
                isVisible={confirmSyncPopup.isVisible}
                title="Sync with Program Version"
                description="Any manually added courses will be removed"
                confirmLabel="Continue"
                loading={syncSections.isLoading}
                onClose={confirmSyncPopup.close}
                onConfirm={onSync}
            />
        </>
    )
}

export default KlassappTableHOC(SectionView)
